import { useState, useEffect } from 'react'
import { useAuthStore } from '@store/authStore'

export interface Location {
  latitude: number
  longitude: number
  accuracy?: number
  source: 'gps' | 'home' | 'default'
}

/**
 * How long to wait for the browser before giving up and using the home location.
 *
 * The Geolocation API takes its own `timeout`, and on paper that is enough. In practice a desktop
 * Chrome with no location provider — no wifi scanning, wired network — can call NEITHER callback,
 * ever: not the success one, not the error one, not even after its own timeout should have fired.
 * The hook then sits on `{ location: null, loading: true }` for the lifetime of the page, and
 * because the fallback to the home location is only reachable from the error callback, it never
 * runs. Every consumer waits forever for a position that is never coming.
 *
 * So the wait is bounded here as well, slightly beyond the API's own timeout so a browser that
 * does answer on time always wins.
 */
const POSITION_WAIT_MS = 8000

interface GeolocationState {
  location: Location | null
  error: string | null
  loading: boolean
  permission: PermissionState | null
}

export function useGeolocation(options?: PositionOptions) {
  const { user } = useAuthStore()
  const [state, setState] = useState<GeolocationState>({
    location: null,
    error: null,
    loading: true,
    permission: null,
  })

  useEffect(() => {
    if (!navigator.geolocation) {
      // Browser doesn't support geolocation, try to use home location
      tryHomeLocation()
      return
    }

    // Check if we're in a secure context (HTTPS or localhost)
    if (!window.isSecureContext) {
      // Silently fall back to home location when not in secure context
      tryHomeLocation()
      return
    }

    // Check permission status
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setState((prev) => ({ ...prev, permission: result.state }))

        result.addEventListener('change', () => {
          setState((prev) => ({ ...prev, permission: result.state }))
        })
      }).catch(() => {
        // Permission query failed, continue anyway
      })
    }

    // Store last reported location to prevent unnecessary updates
    let lastReportedLat: number | null = null
    let lastReportedLng: number | null = null

    // Guards the fallback below: once the browser has answered, in either direction, the timer
    // must not overwrite a real position with the home one.
    let answered = false
    const fallbackTimer = window.setTimeout(() => {
      if (answered) return
      answered = true
      tryHomeLocation()
    }, POSITION_WAIT_MS)
    const SIGNIFICANT_CHANGE_THRESHOLD = 0.00005 // ~5.5 meters at equator for real-time tracking

    // Try to get GPS location - use getCurrentPosition first, then watch
    navigator.geolocation.getCurrentPosition(
      (position) => {
        answered = true
        window.clearTimeout(fallbackTimer)

        lastReportedLat = position.coords.latitude
        lastReportedLng = position.coords.longitude

        setState({
          location: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            source: 'gps',
          },
          error: null,
          loading: false,
          permission: 'granted',
        })
      },
      (error) => {
        answered = true
        window.clearTimeout(fallbackTimer)

        // Only log non-security errors
        if (error.code !== error.PERMISSION_DENIED) {
          console.warn('Geolocation error:', error.message)
        }
        // GPS failed, try to use home location
        tryHomeLocation()
      },
      {
        enableHighAccuracy: false, // Use false for faster initial position
        timeout: 5000,
        maximumAge: 30000,
        ...options,
      }
    )

    // Watch for significant location changes only
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        // Only update if location changed significantly (>111 meters)
        const latChange = lastReportedLat !== null ? Math.abs(position.coords.latitude - lastReportedLat) : Infinity
        const lngChange = lastReportedLng !== null ? Math.abs(position.coords.longitude - lastReportedLng) : Infinity

        if (latChange > SIGNIFICANT_CHANGE_THRESHOLD || lngChange > SIGNIFICANT_CHANGE_THRESHOLD) {
          // A fix that arrives from the watch after the fallback already fired is still the best
          // answer available, and it must also stop the timer from firing later.
          answered = true
          window.clearTimeout(fallbackTimer)

          lastReportedLat = position.coords.latitude
          lastReportedLng = position.coords.longitude

          setState({
            location: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              source: 'gps',
            },
            error: null,
            loading: false,
            permission: 'granted',
          })
        }
      },
      (error) => {
        // Ignore errors from watch - we already handled initial position error
      },
      {
        enableHighAccuracy: true,
        timeout: 30000, // Longer timeout for watch
        maximumAge: 60000, // Cache for 1 minute
        ...options,
      }
    )

    return () => {
      window.clearTimeout(fallbackTimer)
      navigator.geolocation.clearWatch(watchId)
    }
  }, [user])

  const tryHomeLocation = () => {
    // Check if user has a home location set
    if (user?.homeLatitude && user?.homeLongitude) {
      setState({
        location: {
          latitude: user.homeLatitude,
          longitude: user.homeLongitude,
          source: 'home',
        },
        error: null,
        loading: false,
        permission: null,
      })
    } else {
      // No home location, use a default location (center of US)
      setState({
        location: {
          latitude: 39.8283, // Center of continental US
          longitude: -98.5795,
          source: 'default',
        },
        error: 'Location access denied and no home location set',
        loading: false,
        permission: 'denied',
      })
    }
  }

  const requestPermission = async () => {
    if (!navigator.geolocation) {
      return false
    }

    return new Promise<boolean>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => resolve(true),
        () => resolve(false)
      )
    })
  }

  return {
    ...state,
    requestPermission,
  }
}
